import Vue from 'vue';
import Vuex from 'vuex';
import axios, { AxiosResponse } from 'axios';
import { lookupsModule, LookupsState } from '@/Stores/LookupStore';
import { Location, userModule, UserState } from '@/Stores/UserStore';
import router from '@/router';
import routeMapper, { MenuItem } from '@/route-mapping';
import constants from './constants';
import i18n from '@/internationalization';
import { citationModule, CitationState } from '@/Stores/CitationStore';
import { appealModule, AppealState } from '@/Stores/AppealStore';
import { loaderModule, LoaderState } from '@/Stores/LoaderStore';
import { createModule, markRoot } from '@/Stores/StoreTypes';
import { Route } from 'vue-router';
import { LocalizationOverride } from '@/Admin/LocalizationOverrides/LocalizationOverride';
import { Dictionary } from 'vue-router/types/router';
import VatsAPI from '@/Shared/VatsAPI';
import VueI18n from 'vue-i18n';
import { Logger } from './Shared/Logger';
import { deepMerge } from '@/Shared/Utilities';
import { newMenus } from '@/new-menu-items';

Vue.use(Vuex);

export interface RootState {
    userModule: UserState;
    lookupsModule: LookupsState;
    citationModule: CitationState;
    appealModule: AppealState;
    loaderModule: LoaderState;
    menuItems: MenuItem[];
    currentLocale: string;
    localeOverrides: Dictionary<Promise<LocalizationOverride>>;
    currentLocation: Location;
    locationChanged: Promise<boolean>;
    pendingNavigation: Route | null;
}

export type StateKey<TState> = {
    [key in (keyof TState)]: key
};

export type DirtyCheck<TState> = {
    [key in (keyof StateKey<TState>)]?: boolean
};

export type PromiseCacheType<TState> = {
    [key in (keyof StateKey<TState>)]?:
        TState[key] extends Dictionary<infer TItem> ?
            Dictionary<Promise<TItem>> :
            Promise<TState[key]> | null
};

export const prepareMenuItem = async (item: MenuItem, location: Location): Promise<MenuItem> => {
    item = await routeMapper.mapMenu(item, router, location);

    item.children = await Promise.all((item.children || []).map(child => prepareMenuItem(child, location)));

    return item;
};

const storeDefinition = markRoot({
    state: {
        menuItems: [] as MenuItem[],
        currentLocale: i18n.locale,
        currentLocation: {} as Location,
        pendingNavigation: null as Route | null,
        localeOverrides: {},
    } as RootState,
    modules: {
        lookupsModule,
        userModule,
        citationModule,
        appealModule,
        loaderModule
    },
    getters: {
        menuItems(state: RootState) {
            return state.menuItems;
        },
        pendingNavigation(state: RootState) {
            return state.pendingNavigation;
        },
        currentDialect(state: RootState) {
            if (!state.currentLocale) {
                return '';
            }
            if (!state.currentLocation) {
                return state.currentLocale;
            }
            return `${state.currentLocale}-${state.currentLocation.urlSegment}`;
        }
    },
    mutations: {
        setMenuItems(state, params: MenuItem[]) {
            state.menuItems = params;
        },
        setCurrentLocale(state, locale: string) {
            const langTag = document.getElementById('language-tag') as HTMLMetaElement;
            if (langTag) {
                langTag.lang = locale;
            }

            document.cookie = `.AspNetCore.Culture=c=${locale}|uic=${locale};secure;path=/`;

            state.currentLocale = locale;
            if (state.currentLocation) {
                typedStore.actions.getLocaleOverrides({
                    cultureName: locale,
                    location: state.currentLocation.urlSegment
                });
            }
        },
        setLocaleOverride(state, payload: { dialectName: string, overridePromise: Promise<LocalizationOverride> }) {
            const { dialectName: cultureName, overridePromise } = payload;
            state.localeOverrides[cultureName] = overridePromise;
        },
        setCurrentLocation(state, newLocation: Location) {
            state.currentLocation = newLocation;
        },
        setPendingNavigation(state, pendingRoute: Route | null) {
            state.pendingNavigation = pendingRoute;
        },
    },
    actions: {
        async getMenuItems({ state, commit }, location: Location) {
            if (!(await typedStore.modules.userModule.actions.getAuthenticationStatus())) {
                commit('setMenuItems', []);
                return;
            }

            let items = state.menuItems;
            if (!items.length) {
                const response = await axios.get<MenuItem[]>('/api/Settings/MenuItems');
                items = response.data;
            }

            const mergeMenus = (menu: MenuItem[], newMenu: MenuItem[]): MenuItem[] => {
                const matchMenus = (menuItem1: MenuItem, menuItem2: MenuItem): boolean => {
                    return menuItem1.text.toUpperCase() === menuItem2.text.toUpperCase();
                };
                for (const menuItem of menu) {
                    const newItem = newMenu.find(item => matchMenus(item, menuItem));
                    if (newItem) {
                        menuItem.children = mergeMenus(menuItem.children, newItem.children);
                    }
                }

                const newItems = newMenu.filter(item => !menu.find(menuItem => matchMenus(menuItem, item)));
                return menu.concat(newItems);
            };

            items = mergeMenus(items, newMenus);

            const preparationPromise = items.map(item => prepareMenuItem(item, location));
            const preparedMenuItems = await Promise.all(preparationPromise);

            commit('setMenuItems', preparedMenuItems);
            return preparedMenuItems;
        },
        async getLocaleOverrides({ state, commit }, payload: { cultureName: string, location: string }) {
            const { cultureName, location } = payload;
            const dialectName = `${cultureName}-${location}`;

            const useExistingLocalization = () => {
                const existingLocalization = i18n.messages[cultureName];
                // If we can't get any overrides, just give them the existing
                const overridden = deepMerge({}, existingLocalization as any || {});
                return {
                    recID: 0,
                    cultureName,
                    overrides: overridden
                } as LocalizationOverride;
            };

            if (!state.localeOverrides[dialectName]) {
                const url = `${location}/Localizations/Overrides/${cultureName}`;
                const result = VatsAPI.get<LocalizationOverride>(url)
                    .then(item => {
                        if (item.status === 204) {
                            return useExistingLocalization();
                        }
                        // Treated as string in DB/C#; convert back to object
                        const overrides = JSON.parse(item.data.overrides as string);
                        const existingLocalization = i18n.messages[cultureName];
                        // Fill in any missing keys to stop constant override warnings
                        const overridden = deepMerge({}, existingLocalization as any || {}, overrides);
                        item.data.overrides = overridden as any;

                        return item.data;
                    })
                    .catch(error => {
                        if (error.response) {
                            const response = error.response as AxiosResponse<any>;
                            if (response.status !== 404) {
                                const msg = `Error getting overrides for ${cultureName}: ${JSON.stringify(response.data)}`;
                                Logger.Error(msg);
                            }
                        }

                        return useExistingLocalization();
                    });
                commit('setLocaleOverride', { dialectName, overridePromise: result });
            }

            const override = await state.localeOverrides[dialectName];
            i18n.setLocaleMessage(dialectName, override.overrides as VueI18n.LocaleMessageObject);

            return override;
        },
        async refreshCurrentLocation({ state }) {
            const refreshedLocation =
                await VatsAPI.get<Location>(`LocationConfig/${state.currentLocation.locationId}`)
                .then(resp => resp.data);
            typedStore.mutations.setCurrentLocation({} as Location);

            await typedStore.actions.changeCurrentLocation(refreshedLocation);
        },
        async changeCurrentLocation({ state }, newLocation: Location) {
            if (state.currentLocation.locationId === newLocation.locationId) {
                return;
            }

            typedStore.mutations.setCurrentLocation(newLocation);

            const newParams = {
                ...router.currentRoute.params,
                location: newLocation.urlSegment
            };
            const routeReload = {
                name: router.currentRoute.name == null ? undefined : router.currentRoute.name,
                params: newParams,
                query: router.currentRoute.query,
            };

            document.cookie = `${constants.DatabaseCookieName}=${newLocation.databaseName};secure;path=/`;

            if (state.currentLocale) {
                typedStore.actions.getLocaleOverrides({
                    cultureName: state.currentLocale,
                    location: newLocation.urlSegment
                });
            }

            typedStore.actions.getMenuItems(newLocation);

            try {
                if (router.currentRoute.matched.length) {
                    await router.push(routeReload);
                }
            }
            catch (reason) {
                // Ignore Navigation Duplicated errors
                // They get thrown when the props change but we don't move to a new page
                if (!reason || reason.name !== 'NavigationDuplicated') {
                    throw reason;
                }
            }
        }
    },
});

const store = new Vuex.Store<RootState>(storeDefinition);
store.watch((rootState, getters) => getters.currentDialect, (newDialect: string) => { i18n.locale = newDialect; });
store.watch(rootState => rootState.currentLocation, () => {
    typedStore.modules.userModule.actions.getCurrentRoles();
});

export const typedStore = createModule(storeDefinition, '', store);

export default store;
